import {Card, DataStatus} from 'platform/components';
import {Box, Grid, GridItem, Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import styled from 'styled-components';

import {head, isEmpty, isNil, isNotEmpty, isNotNil, last} from 'ramda';

import {useGetPriceSnapshotsQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  DmsDataGrid,
  PriceChart,
  PriceChartItem,
  refreshDmsDatagrid,
  usePricePermissions,
} from '@dms/shared';

import {parseDate, useRequiredParams} from 'shared/web';

import type {QueryFilterObject} from 'features/eag-grid';

import {OverviewCard} from './OverviewCard';

export function VehiclePrices() {
  const {id: vehicleId} = useRequiredParams();
  const refreshDataGrid = () => refreshDmsDatagrid('sale_vehicle_price_snapshot_dg');
  const formatCurrency = useFormatCurrency();
  const {canReadVehicleSellingPrice} = usePricePermissions({
    vehicleRecordId: vehicleId,
    businessCaseRecordId: null,
  });
  const {
    data: priceSnapshots,
    isLoading: isLoadingPriceSnapshots,
    isError: isErrorPriceSnapshots,
    fulfilledTimeStamp,
  } = useGetPriceSnapshotsQuery({vehicleId}, {skip: !canReadVehicleSellingPrice});

  const queryModifier = (filter: QueryFilterObject) => ({
    ...filter,
    vehicleId,
  });

  const snapshotPoints = (priceSnapshots?.snapshots ?? [])
    .map((snapshot) => ({
      date: parseDate(snapshot.createdAt)?.getTime(),
      price: Number(snapshot.sellingPriceWithVat?.amount),
    }))
    .filter((snapshot) => Number.isFinite(snapshot.price) && isNotNil(snapshot.date));
  const lastSnapshotPoint = last(snapshotPoints);
  const chartEndDate = fulfilledTimeStamp ?? 0;
  const chartData: PriceChartItem[] = isNotEmpty(snapshotPoints)
    ? [
        ...snapshotPoints,
        {
          date: chartEndDate,
          price: lastSnapshotPoint?.price ?? 0,
        },
      ]
    : snapshotPoints;
  const currency = priceSnapshots?.snapshots[0]?.sellingPriceWithVat?.currency ?? '';
  const totalDiscount = isNotNil(priceSnapshots?.snapshots)
    ? Number(last(priceSnapshots.snapshots)?.sellingPriceWithVat?.amount) -
      Number(head(priceSnapshots.snapshots)?.sellingPriceWithVat?.amount)
    : 0;
  const numberOfRepricings =
    isNil(priceSnapshots?.snapshots) || isEmpty(priceSnapshots.snapshots)
      ? 0
      : priceSnapshots.snapshots.length - 1;

  return (
    <VStack spacing={4}>
      <OverviewCard
        onEdit={refreshDataGrid}
        vehicleId={vehicleId}
        data-testid={testIds.vehicles.finances('overview')}
      />
      <Show when={canReadVehicleSellingPrice}>
        <Card title={i18n.t('entity.vehicle.labels.priceProgress')}>
          <Box height={40}>
            <DataStatus
              isLoading={isLoadingPriceSnapshots}
              isError={isErrorPriceSnapshots}
              isEmpty={isEmpty(chartData)}
              minHeight="100%"
            >
              <Grid columns={[1, 1, 4]} minHeight="100%">
                <GridItem span={[1, 1, 3]}>
                  <PriceChart chartData={chartData} currency={currency} marginPercentage={10} />
                </GridItem>
                <VStack spacing={4}>
                  <Text size="xSmall" color="secondary">
                    {i18n.t('entity.vehicle.labels.totalDiscount')}
                  </Text>
                  <HStack spacing={3} align="center">
                    <PriceChangeBox $colorScheme={totalDiscount <= 0 ? 'orange' : 'red'}>
                      <Icon
                        value={totalDiscount <= 0 ? 'action/trending_down' : 'action/trending_up'}
                        size={3}
                        color="general.white"
                      />
                    </PriceChangeBox>
                    <Heading size={3} color="primary">
                      {formatCurrency(totalDiscount, currency)}
                    </Heading>
                  </HStack>
                  <Text size="xSmall" color="secondary">
                    {i18n.t('entity.vehicle.labels.numberOfRepricings')}
                  </Text>
                  <Heading size={3} color="primary">
                    {String(numberOfRepricings)}
                  </Heading>
                </VStack>
              </Grid>
            </DataStatus>
          </Box>
        </Card>
      </Show>
      <Card title={i18n.t('entity.vehicle.labels.priceHistory')}>
        <Box height={80}>
          <DmsDataGrid
            gridCode="sale_vehicle_price_snapshot_dg"
            queryModifier={queryModifier}
            data-testid={testIds.vehicles.finances('priceHistory')}
          />
        </Box>
      </Card>
    </VStack>
  );
}

type PriceChangeBoxProps = {
  $colorScheme: 'orange' | 'red';
};

// General Box component doesn't allow to set 2px padding
// eslint-disable-next-line eag/no-css-property
const PriceChangeBox = styled.div<PriceChangeBoxProps>`
  background-color: ${(props) => props.theme.colors.palettes[props.$colorScheme][60][100]};
  border-radius: ${(props) => props.theme.radii.xSmall};
  padding: 2px;
`;
